/**
 * [비즈니스 목적]
 * LLM 변환 가능한 파일 온톨로지 데이터를 조회하고 관리하는 커스텀 훅
 *
 * [워크플로우]
 * 1. 테넌트 선택 시 API를 통해 LLM 변환 가능한 파일 목록 조회
 * 2. 파일 데이터를 UI 표시 형식으로 변환 (useMemo로 최적화)
 * 3. 파일 정보 캐싱 및 refetch 기능 제공
 *
 * [주요 기능]
 * - LLM 변환 가능한 파일만 필터링하여 조회
 * - 파일 ID, 표시명 정규화 (standard_file_name 우선 사용)
 * - 로딩 상태 및 에러 처리
 * - 테넌트 변경 시 자동 재조회
 * - 수동 refetch 기능
 *
 * [반환 값]
 * - files: 변환된 파일 목록 배열 [{ file_id, displayName, originalFile }]
 * - loading: 로딩 상태 (boolean)
 * - error: 에러 메시지 (string|null)
 * - refetch: 수동 재조회 함수
 *
 * [데이터 변환]
 * - file_id: file.file_id || file.fileId || file.id
 * - displayName: file.standard_file_name || file.standardFileName || file.fileName || '파일_{ID 일부}'
 * - originalFile: 원본 파일 객체 보존
 *
 * [사용 예시]
 * const { files, loading, refetch } = useFileOntology(tenantCode);
 *
 * useEffect(() => {
 *   if (files.length > 0) {
 *     console.log('파일 로드됨:', files);
 *   }
 * }, [files]);
 */

import { useState, useEffect, useMemo } from 'react';
import apiService from '../../../services/api';

export const useFileOntology = (selectedTenant) => {
  const [ontologyFiles, setOntologyFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch LLM-enabled files when tenant changes
  const fetchFiles = async () => {
    if (!selectedTenant) {
      setOntologyFiles([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use getLlmEnabledFiles to only show LLM-convertible files
      const filesResponse = await apiService.getLlmEnabledFiles(selectedTenant);
      if (filesResponse.success) {
        setOntologyFiles(filesResponse.data);
      } else {
        console.error('Failed to fetch LLM-enabled files:', filesResponse.error);
        setOntologyFiles([]);
        setError(filesResponse.error);
      }
    } catch (err) {
      console.error('Error fetching LLM-enabled files:', err);
      setOntologyFiles([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [selectedTenant]);

  // Convert ontology files to display format
  const files = useMemo(() => {
    return ontologyFiles.map(file => {
      const fileId = file.file_id || file.fileId || file.id;
      const displayName = file.standard_file_name || file.standardFileName || file.fileName || (fileId ? `파일_${fileId.substring(0, 8)}` : '이름없음');

      return {
        file_id: fileId,
        displayName,
        originalFile: file
      };
    });
  }, [ontologyFiles]);

  return {
    files,
    loading,
    error,
    refetch: fetchFiles
  };
};
